import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";

/**
 * PageTransition - Transition cinématique entre les pages
 * - Fade + léger slide-up au changement de route
 * - Barre de progression or en haut de page pendant la transition
 * - Scroll to top instantané à chaque navigation
 */
export default function PageTransition({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const prevLocation = useRef(location);
  const t1 = useRef<ReturnType<typeof setTimeout> | null>(null);
  const t2 = useRef<ReturnType<typeof setTimeout> | null>(null);
  const t3 = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (prevLocation.current === location) return;
    prevLocation.current = location;

    // Nettoyer les timers précédents
    if (t1.current) clearTimeout(t1.current);
    if (t2.current) clearTimeout(t2.current);
    if (t3.current) clearTimeout(t3.current);

    // Démarrer la barre de progression
    setProgress(40);
    setVisible(false);

    t1.current = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
      setProgress(80);
    }, 130);

    t2.current = setTimeout(() => {
      setProgress(100);
      setVisible(true);
    }, 210);

    t3.current = setTimeout(() => {
      setProgress(0);
    }, 650);

    return () => {
      if (t1.current) clearTimeout(t1.current);
      if (t2.current) clearTimeout(t2.current);
      if (t3.current) clearTimeout(t3.current);
    };
  }, [location]);

  return (
    <>
      {/* Barre de progression or en haut de page */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "2px",
          width: `${progress}%`,
          background: "linear-gradient(90deg, #A61E2D, #C9A84C)",
          zIndex: 99997,
          transition: progress === 0
            ? "none"
            : progress === 100
              ? "width 0.2s ease, opacity 0.3s ease 0.2s"
              : "width 0.3s ease",
          opacity: progress === 0 || progress === 100 ? 0 : 1,
          pointerEvents: "none",
        }}
      />

      {/* Contenu avec fade + slide-up */}
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0px)" : "translateY(12px)",
          transition: visible
            ? "opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1), transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)"
            : "opacity 0.13s ease, transform 0.13s ease",
          willChange: "opacity, transform",
        }}
      >
        {children}
      </div>
    </>
  );
}
